import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { loadPbrMaps, disposePbrMaps, ensureUv2, getPbrLayout, PBR_SETS, PAINT_SET_KEY } from './pbrTextures.jsx';

// «المان» پنجره‌ی سه‌بعدی — دقیقاً همان سیستمِ در (Door3D.jsx): چند استایلِ قابل‌انتخاب، هرکدام با
// نحوه‌ی بازشدنِ متناسبِ خودش، رنگِ قاب/شیشه و عرض/ارتفاع/ارتفاعِ‌از‌کفِ قابل‌تنظیم، و قابلیتِ
// انتخاب (تپ‌کردن در حالتِ اوربیت) + بازشدن با لمسِ نشانه‌ی دست در حالتِ اول‌شخص (هر دو در
// Create3D.jsx مدیریت می‌شوند، دقیقاً مثلِ در):
//   • ثابت (fixed) — اصلاً باز نمی‌شود، فقط شیشه+قاب، بدون دستگیره و بدون نشانه‌ی دست
//   • لولاییِ کناری (casement) / دولنگه‌ی لولایی (doubleCasement) — مثلِ در، لولا کنارِ پنجره
//   • بازشوِ بالازن (awning) — لولا بالای پنجره، لبه‌ی پایین کج می‌شود
//   • بازشوِ پایین‌زن/تهویه (hopper) — لولا پایینِ پنجره، لبه‌ی بالا کج می‌شود
//   • کشویی (sliding) — برخلافِ درِ کشویی، این‌جا لنگه از قابِ خودِ پنجره بیرون نمی‌رود؛ دو لنگه با
//     دو ریلِ کمی متفاوت (یکی کمی جلوتر) دارند، یکی ثابت می‌ماند و دیگری پشتِ آن سُر می‌خورد
// برخلافِ نسخه‌ی قبلی، جهتِ بازشوهای لولایی/کج‌شونده (casement/doubleCasement/awning/hopper) دیگر
// ثابت نیست — window.style.facing ('in' یا 'out'، دقیقاً مثلِ door.facing) تعیین می‌کند به کدام
// سمت باز شوند؛ کشویی و ثابت این تنظیم را ندارند چون برایشان معنا ندارد

const DEFAULT_FRAME_COLOR = '#f4f4f2';
const DEFAULT_GLASS_COLOR = '#dce8f0';
const WINDOW_OPEN_ANGLE = Math.PI * 0.4; // ~۷۲ درجه — کمتر از در، چون پنجره‌ها معمولاً این‌قدر باز نمی‌شوند
const WINDOW_SWING_SPEED = 6;

// ابعادِ واقعی (سانتی‌متر) — ثابت، نه درصدی از اندازه‌ی پنجره؛ درصدی بودن باعث می‌شد پنجره‌ی بزرگ
// قاب‌های کلفت و پنجره‌ی کوچک قاب‌های نخی داشته باشد و کلِ پنجره «کارتونی» دیده شود
const OUTER_FRAME_CM = 5; // پروفیلِ قابِ ثابتِ دورِ پنجره (نمای روبه‌رو)
const SASH_PROFILE_CM = 5.5; // پروفیلِ قابِ لنگه
const SASH_GAP_CM = 0.3; // درزِ لنگه با قابِ ثابت
const GLASS_THICKNESS_CM = 2.4; // شیشه‌ی دوجداره
const SILL_THICKNESS_CM = 2; // سنگِ زیرِ پنجره (داخل و بیرون)
const SILL_PROJECTION_CM = 3; // بیرون‌زدگیِ سنگ از سطحِ دیوار
const SILL_EARS_CM = 4; // سنگ از هر طرف این‌قدر زیرِ دیوارِ کناری می‌رود (مثلِ اجرای واقعی)
const SILL_SET_KEY = 'marbleVeined';
const WALL_FACE_OFFSET_CM = 0.4; // همان FACE_OFFSET_CM در Wall3D.jsx

export const DEFAULT_WINDOW_STYLE = {
  type: 'sliding',
  color: DEFAULT_FRAME_COLOR,
  accent: DEFAULT_GLASS_COLOR,
  widthCm: null,
  heightCm: null,
  sillCm: null,
  facing: 'out',
  material: 'paint',
};

// window.style ممکن است ناقص باشد — همیشه یک شیءِ کاملِ با پیش‌فرض برمی‌گرداند، دقیقاً مثلِ
// resolveDoorStyle در Door3D.jsx
export function resolveWindowStyle(win) {
  const s = win?.style;
  return {
    type: s?.type || DEFAULT_WINDOW_STYLE.type,
    color: s?.color || DEFAULT_WINDOW_STYLE.color,
    accent: s?.accent || DEFAULT_WINDOW_STYLE.accent,
    widthCm: s?.widthCm || null,
    heightCm: s?.heightCm || null,
    sillCm: s?.sillCm != null ? s.sillCm : null,
    facing: s?.facing || DEFAULT_WINDOW_STYLE.facing,
    material: s?.material || DEFAULT_WINDOW_STYLE.material,
  };
}

// نوعِ جنسِ قاب ('paint' یعنی همان رنگِ ساده‌ی قبلی) → کلیدِ متناظر در PBR_SETS (pbrTextures.jsx).
// دقیقاً همان الگویِ PBR_FLOOR_TYPE_TO_SET/PBR_WALL_TYPE_TO_SET — اضافه‌کردنِ جنسِ واقعیِ بعدی (مثلاً
// یک فلزِ دیگر) فقط یک خط این‌جا + یک ردیف در PBR_SETS نیاز دارد
export const PBR_FRAME_MATERIAL_TO_SET = {
  metalBrushed: 'metalBrushed',
  woodPine: 'woodPine',
  woodDark: 'woodDark',
};

// یک نبشیِ تکی از قاب (بالا/پایین/چپ/راست) — چون هرکدام ابعادِ واقعیِ متفاوتی دارند، هرکدام باید
// نقشه‌ی PBR خودش را با تکرارِ مخصوصِ خودش بسازد (دقیقاً همان «هر Texture برای یک مصرف» که در
// pbrTextures.jsx تشریح شده)؛ برای همین به یک کامپوننتِ جدا تبدیل شده، نه فقط یک <mesh> ساده —
// هوک‌ها (useMemo/useEffect) باید در سطحِ خودِ کامپوننت باشند، نه داخلِ حلقه/تابعِ کمکی.
// وقتی frameMaterial جنسِ ساده (رنگ) باشد، دقیقاً همان رفتارِ قبلی حفظ می‌شود؛ وقتی جنسِ واقعیِ
// عکس (مثلِ 'metalBrushed') باشد، رنگِ انتخابیِ کاربر همچنان روی خودِ عکس «ضرب» می‌شود
// layoutCm (اختیاری): ابعادِ رویه‌ی اصلیِ دیده‌شده برای مقیاسِ بافت (مثلاً سنگِ زیرِ پنجره که رویه‌ی
// اصلی‌اش بالای آن است نه جلویش). setKey (اختیاری): یک ستِ PBR مشخص، مستقل از جنسِ قاب
function FrameBar({ position, sizeCm, layoutCm, frameColor, frameMaterial, setKey }) {
  const isPbr = Boolean(setKey || PBR_FRAME_MATERIAL_TO_SET[frameMaterial]);
  // «رنگ» هم رنگِ واقعی است: بافتِ سطحِ رنگ‌شده (فقط برجستگی) با همان زبریِ قبلیِ قابِ رنگی
  const isPaint = !isPbr;
  const pbrSet = PBR_SETS[setKey || (isPbr ? PBR_FRAME_MATERIAL_TO_SET[frameMaterial] : PAINT_SET_KEY)];
  // نوارهای افقیِ قاب (بالا/پایین) برای چوب، عکس را می‌چرخانند تا رگه در امتدادِ طولِ نوار باشد
  const layout = layoutCm || sizeCm;
  const { repeatX, repeatY, rotate } = getPbrLayout(pbrSet, layout[0], layout[1]);
  // فقط فلز (ستِ دارای metalnessMap) فلزی است؛ چوب metalness=0
  const pbrMetalness = pbrSet?.metalness ? 1 : 0;

  const pbrMaps = useMemo(
    () => loadPbrMaps(pbrSet, repeatX, repeatY, { rotate }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPbr, frameMaterial, setKey, repeatX, repeatY, rotate]
  );

  useEffect(() => () => disposePbrMaps(pbrMaps), [pbrMaps]);

  return (
    <mesh position={position}>
      <boxGeometry ref={ensureUv2} args={sizeCm} />
      <meshStandardMaterial
        key={isPbr ? `pbr-${setKey || frameMaterial}` : 'paint'}
        color={frameColor}
        map={pbrMaps?.map || null}
        normalMap={pbrMaps?.normalMap || null}
        roughnessMap={isPaint ? null : pbrMaps?.roughnessMap || null}
        aoMap={pbrMaps?.aoMap || null}
        metalnessMap={isPbr ? pbrMaps?.metalnessMap || null : null}
        roughness={isPbr ? 1 : 0.5}
        metalness={isPbr ? pbrMetalness : 0}
        envMapIntensity={isPbr ? 1 : 0.2}
      />
    </mesh>
  );
}

// قابِ توخالی (۴ نبشیِ نازک دورتادور) + یک شیشه‌ی نیمه‌شفافِ وسط — واحدِ بصریِ مشترکِ همه‌ی انواعِ
// پنجره؛ تفاوتِ اصلیِ انواع، نحوه‌ی حرکت/چرخشِ همین واحد است، نه ظاهرش. frameMaterial پیش‌فرضش
// 'paint' است (رفتارِ قبلی، رنگِ ساده) و اختیاری‌ست تا فراخوانی‌های قدیمی نشکنند
function WindowPane({ paneWidth, paneHeight, paneThickness, frameColor, glassColor, frameMaterial = 'paint' }) {
  // پروفیلِ ثابتِ ۵٫۵ سانتی (فقط در لنگه‌های خیلی کوچک کمتر، تا شیشه جا داشته باشد)
  const frameW = Math.min(SASH_PROFILE_CM, Math.min(paneWidth, paneHeight) * 0.2);
  const innerW = Math.max(1, paneWidth - frameW * 2);
  const innerH = Math.max(1, paneHeight - frameW * 2);
  return (
    <group>
      <FrameBar
        position={[0, (paneHeight - frameW) / 2, 0]}
        sizeCm={[paneWidth, frameW, paneThickness]}
        frameColor={frameColor}
        frameMaterial={frameMaterial}
      />
      <FrameBar
        position={[0, -(paneHeight - frameW) / 2, 0]}
        sizeCm={[paneWidth, frameW, paneThickness]}
        frameColor={frameColor}
        frameMaterial={frameMaterial}
      />
      <FrameBar
        position={[(paneWidth - frameW) / 2, 0, 0]}
        sizeCm={[frameW, paneHeight - frameW * 2, paneThickness]}
        frameColor={frameColor}
        frameMaterial={frameMaterial}
      />
      <FrameBar
        position={[-(paneWidth - frameW) / 2, 0, 0]}
        sizeCm={[frameW, paneHeight - frameW * 2, paneThickness]}
        frameColor={frameColor}
        frameMaterial={frameMaterial}
      />
      <mesh position={[0, 0, 0]}>
        <boxGeometry args={[innerW + 0.6, innerH + 0.6, Math.min(GLASS_THICKNESS_CM, paneThickness * 0.5)]} />
        <meshPhysicalMaterial
          color={glassColor}
          transparent
          opacity={0.3}
          roughness={0.05}
          metalness={0}
          ior={1.5}
          envMapIntensity={0.9}
        />
      </mesh>
    </group>
  );
}

// نکته‌ی مشترکِ همه‌ی لنگه‌های لولایی/کج‌شونده: محورِ لولا روی «رویه‌ی» قابِ ثابت است، در همان سمتی که
// لنگه به آن باز می‌شود (pivotZ)، و خودِ لنگه به‌اندازه‌ی نصفِ ضخامتش به داخلِ قاب جابه‌جا شده (contentZ) —
// مثلِ پنجره‌ی واقعی. قبلاً محور وسطِ ضخامت بود و لبه‌ی عقبیِ لنگه موقعِ باز شدن داخلِ قابِ کناری فرو می‌رفت

// لنگه‌ی لولاییِ کناری — گروهِ بیرونی روی نقطه‌ی لولا می‌چرخد، محتوا با hingeDir از آن فاصله می‌گیرد.
// swingSign جدا از hingeDir است تا برای دولنگه بشود دو طرف را با علامتِ مخالف چرخاند
function CasementSash({ hingeX, hingeDir, swingSign, sashWidth, height, sashThickness, pivotZ, contentZ, style, targetAngle }) {
  const groupRef = useRef(null);
  const angleRef = useRef(0);

  useFrame((_, delta) => {
    const diff = targetAngle - angleRef.current;
    angleRef.current = Math.abs(diff) < 0.001 ? targetAngle : angleRef.current + diff * Math.min(1, delta * WINDOW_SWING_SPEED);
    if (groupRef.current) groupRef.current.rotation.y = angleRef.current * swingSign;
  });

  const contentX = hingeDir * (sashWidth / 2);
  const handleX = hingeDir * (sashWidth / 2 - SASH_PROFILE_CM / 2);

  return (
    <group ref={groupRef} position={[hingeX, 0, pivotZ]}>
      <group position={[contentX, 0, contentZ]}>
        <WindowPane paneWidth={sashWidth} paneHeight={height} paneThickness={sashThickness} frameColor={style.color} glassColor={style.accent} frameMaterial={style.material} />
        <mesh position={[handleX, 0, sashThickness / 2 + 1.5]}>
          <boxGeometry args={[2, 9, 1.4]} />
          <meshStandardMaterial color="#c9c9c9" roughness={0.3} metalness={0.6} />
        </mesh>
      </group>
    </group>
  );
}

// لنگه‌ی کج‌شونده — «بالازن» (لولا بالا) و «پایین‌زن» (لولا پایین). چرخشِ واقعی angle·dirSign·contentDir
// است تا هر دو نوع با یک facing به یک سمتِ فیزیکی کج شوند (توضیحِ کامل در نسخه‌ی قبلی)
function TiltSash({ hingeY, contentDir, dirSign, width, height, sashThickness, pivotZ, contentZ, style, targetAngle }) {
  const groupRef = useRef(null);
  const angleRef = useRef(0);

  useFrame((_, delta) => {
    const diff = targetAngle - angleRef.current;
    angleRef.current = Math.abs(diff) < 0.001 ? targetAngle : angleRef.current + diff * Math.min(1, delta * WINDOW_SWING_SPEED);
    if (groupRef.current) groupRef.current.rotation.x = angleRef.current * dirSign * contentDir;
  });

  const contentY = contentDir * (height / 2);
  const handleY = contentDir * (height / 2 - SASH_PROFILE_CM / 2);

  return (
    <group ref={groupRef} position={[0, hingeY, pivotZ]}>
      <group position={[0, contentY, contentZ]}>
        <WindowPane paneWidth={width} paneHeight={height} paneThickness={sashThickness} frameColor={style.color} glassColor={style.accent} frameMaterial={style.material} />
        <mesh position={[0, handleY, sashThickness / 2 + 1.5]}>
          <boxGeometry args={[9, 2, 1.4]} />
          <meshStandardMaterial color="#c9c9c9" roughness={0.3} metalness={0.6} />
        </mesh>
      </group>
    </group>
  );
}

// کشویی — دو لنگه روی دو ریلِ جدا (یکی جلوتر، یکی عقب‌تر) داخلِ قابِ ثابت. لنگه‌ها وسط ۴ سانت روی هم
// می‌افتند (مثلِ پنجره‌ی کشوییِ واقعی که وسطش درز ندارد). لنگه‌ی راست ثابت است؛ لنگه‌ی چپ دقیقاً تا جایی
// می‌لغزد که پشتِ لنگه‌ی ثابت قرار بگیرد
const SLIDING_OVERLAP_CM = 4;
function SlidingWindowSash({ width, height, sashThickness, style, isOpen }) {
  const groupRef = useRef(null);
  const offsetRef = useRef(0);
  const sashW = width / 2 + SLIDING_OVERLAP_CM / 2;
  const restX = -width / 2 + sashW / 2;
  const fixedX = width / 2 - sashW / 2;
  const targetOffset = isOpen ? fixedX - restX : 0;
  const trackZ = sashThickness / 2 + 0.3;

  useFrame((_, delta) => {
    const diff = targetOffset - offsetRef.current;
    offsetRef.current =
      Math.abs(diff) < 0.001 ? targetOffset : offsetRef.current + diff * Math.min(1, delta * WINDOW_SWING_SPEED);
    if (groupRef.current) groupRef.current.position.x = restX + offsetRef.current;
  });

  return (
    <group>
      <group position={[fixedX, 0, trackZ]}>
        <WindowPane paneWidth={sashW} paneHeight={height} paneThickness={sashThickness} frameColor={style.color} glassColor={style.accent} frameMaterial={style.material} />
      </group>
      <group ref={groupRef} position={[restX, 0, -trackZ]}>
        <WindowPane paneWidth={sashW} paneHeight={height} paneThickness={sashThickness} frameColor={style.color} glassColor={style.accent} frameMaterial={style.material} />
        <mesh position={[sashW / 2 - SASH_PROFILE_CM / 2, 0, -sashThickness / 2 - 0.8]}>
          <boxGeometry args={[2, 9, 1.4]} />
          <meshStandardMaterial color="#c9c9c9" roughness={0.3} metalness={0.6} />
        </mesh>
      </group>
    </group>
  );
}

// پنجره‌ی ثابت — شیشه مستقیم داخلِ قابِ ثابت (بدونِ لنگه، بدونِ دستگیره)
function FixedGlass({ width, height, glassColor }) {
  return (
    <mesh>
      <boxGeometry args={[width + 0.6, height + 0.6, GLASS_THICKNESS_CM]} />
      <meshPhysicalMaterial
        color={glassColor}
        transparent
        opacity={0.3}
        roughness={0.05}
        metalness={0}
        ior={1.5}
        envMapIntensity={0.9}
      />
    </mesh>
  );
}

// قابِ ثابتِ دورِ پنجره + سنگِ زیرِ پنجره (هر دو سمتِ دیوار) — هیچ‌وقت حرکت نمی‌کند. قاب دقیقاً لبه‌های
// بازشو را می‌پوشاند و لنگه‌ها داخلِ آن با درزِ ثابتِ ۳ میلی‌متر قرار می‌گیرند
function OuterFrame({ width, height, depth, thickness, style }) {
  const F = OUTER_FRAME_CM;
  const bar = { frameColor: style.color, frameMaterial: style.material };
  const sillDepth = thickness / 2 + WALL_FACE_OFFSET_CM + SILL_PROJECTION_CM - depth / 2;
  const sillW = width + SILL_EARS_CM * 2;
  return (
    <group>
      <FrameBar position={[0, height / 2 - F / 2, 0]} sizeCm={[width, F, depth]} {...bar} />
      <FrameBar position={[0, -height / 2 + F / 2, 0]} sizeCm={[width, F, depth]} {...bar} />
      <FrameBar position={[width / 2 - F / 2, 0, 0]} sizeCm={[F, height - F * 2, depth]} {...bar} />
      <FrameBar position={[-width / 2 + F / 2, 0, 0]} sizeCm={[F, height - F * 2, depth]} {...bar} />
      {sillDepth > 0 &&
        [-1, 1].map((sz) => (
          <FrameBar
            key={sz}
            position={[0, -height / 2 + SILL_THICKNESS_CM / 2, sz * (depth / 2 + sillDepth / 2)]}
            sizeCm={[sillW, SILL_THICKNESS_CM, sillDepth]}
            layoutCm={[sillW, sillDepth]}
            frameColor="#ffffff"
            setKey={SILL_SET_KEY}
          />
        ))}
    </group>
  );
}

// centerHeight: ارتفاعِ مرکز پنجره از کف. width/height اندازه‌ی بازشوی برش‌خورده در دیوار است.
// isOpen برای «ثابت» عمداً بی‌اثر است
function Window({ width, height, thickness, centerAlong, centerHeight, style, isOpen, isSelected, onSelect }) {
  if (width <= 0 || height <= 0) return null;
  const s = style || DEFAULT_WINDOW_STYLE;
  const targetAngle = isOpen ? WINDOW_OPEN_ANGLE : 0;
  // 'in'/'out' — فقط برای انواعِ لولایی/کج‌شونده معنا دارد
  const dirSign = s.facing === 'in' ? 1 : -1;

  // عمقِ قابِ ثابت (پروفیلِ ۷ سانتی، در دیوارِ نازک کمتر) و فضای داخلِ آن
  const frameDepth = Math.max(4, Math.min(7, thickness - 4));
  const innerW = Math.max(4, width - OUTER_FRAME_CM * 2);
  const innerH = Math.max(4, height - OUTER_FRAME_CM * 2);
  const sashThickness = Math.max(2.5, frameDepth - 1);
  const sashW = innerW - SASH_GAP_CM * 2;
  const sashH = innerH - SASH_GAP_CM * 2;
  // لنگه‌ی کناری وقتی swingSign=+1 به سمتِ -Z می‌چرخد؛ لنگه‌ی کج‌شونده به سمتِ +dirSign
  const casementOpenZ = -dirSign;
  const tiltOpenZ = dirSign;
  const casementPivot = { pivotZ: casementOpenZ * (frameDepth / 2), contentZ: -casementOpenZ * (sashThickness / 2) };
  const tiltPivot = { pivotZ: tiltOpenZ * (frameDepth / 2), contentZ: -tiltOpenZ * (sashThickness / 2) };

  const handleClick = (e) => {
    e.stopPropagation();
    if (onSelect) onSelect();
  };

  let content;
  if (s.type === 'casement') {
    content = (
      <CasementSash
        hingeX={-innerW / 2 + SASH_GAP_CM}
        hingeDir={1}
        swingSign={dirSign}
        sashWidth={sashW}
        height={sashH}
        sashThickness={sashThickness}
        style={s}
        targetAngle={targetAngle}
        {...casementPivot}
      />
    );
  } else if (s.type === 'doubleCasement') {
    const half = (innerW - SASH_GAP_CM * 3) / 2;
    content = (
      <>
        <CasementSash
          hingeX={-innerW / 2 + SASH_GAP_CM}
          hingeDir={1}
          swingSign={dirSign}
          sashWidth={half}
          height={sashH}
          sashThickness={sashThickness}
          style={s}
          targetAngle={targetAngle}
          {...casementPivot}
        />
        <CasementSash
          hingeX={innerW / 2 - SASH_GAP_CM}
          hingeDir={-1}
          swingSign={-dirSign}
          sashWidth={half}
          height={sashH}
          sashThickness={sashThickness}
          style={s}
          targetAngle={targetAngle}
          {...casementPivot}
        />
      </>
    );
  } else if (s.type === 'awning' || s.type === 'hopper') {
    const isAwning = s.type === 'awning';
    content = (
      <TiltSash
        hingeY={isAwning ? innerH / 2 - SASH_GAP_CM : -innerH / 2 + SASH_GAP_CM}
        contentDir={isAwning ? -1 : 1}
        dirSign={dirSign}
        width={sashW}
        height={sashH}
        sashThickness={sashThickness}
        style={s}
        targetAngle={targetAngle}
        {...tiltPivot}
      />
    );
  } else if (s.type === 'sliding') {
    content = (
      <SlidingWindowSash
        width={innerW}
        height={innerH}
        sashThickness={Math.max(2.5, (frameDepth - 1) / 2)}
        style={s}
        isOpen={isOpen}
      />
    );
  } else {
    content = <FixedGlass width={innerW} height={innerH} glassColor={s.accent} />;
  }

  return (
    <group position={[centerAlong, centerHeight, 0]} onClick={handleClick}>
      {isSelected && (
        <mesh position={[0, height / 2 + 6, 0]}>
          <boxGeometry args={[width * 0.98, 3, thickness + 4]} />
          <meshBasicMaterial color="#0a66c2" />
        </mesh>
      )}
      {/* هدفِ لمسِ نامرئی — کلِ بازشوی پنجره را می‌پوشاند تا لمسِ هر نقطه (حتی وسطِ شیشه) پنجره را انتخاب کند */}
      <mesh position={[0, 0, 0]} onClick={handleClick}>
        <boxGeometry args={[width, height, thickness + 6]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      <OuterFrame width={width} height={height} depth={frameDepth} thickness={thickness} style={s} />
      {/* khLeaf: لنگه‌ها/شیشه — در نمایشِ «رندرِ واقعی» نسخه‌ی زنده (باز/بسته‌شونده) دیده می‌شود */}
      <group userData={{ khLeaf: true }}>{content}</group>
    </group>
  );
}

export default Window;
